import React, { useState, useEffect, useRef } from 'react';
import { firestore } from '../../firebase/config';
import { collection, query, where, getDocs } from 'firebase/firestore';
import '../../pages/Dashboard.css';
import * as XLSX from 'xlsx';
import { useTodasLocalidades } from '../../hooks/useLocalidades';

// --- Função Auxiliar para Normalização ---
const normalizarTexto = (texto) => {
    if (!texto) return '';
    return String(texto).trim().toUpperCase();
};

// --- Constantes para Opções de Filtro ---
const OPCOES_STATUS = [
    { label: 'Trabalhado', value: 'INSPECIONADO' },
    { label: 'Recuperado', value: 'RECUPERADO' },
    { label: 'Recusa', value: 'RECUSA' },
    { label: 'Fechado', value: 'FECHADO' },
    { label: 'Aguardando Análise', value: 'AGUARDANDO ANÁLISE' },
    { label: 'Análise Parcial', value: 'ANÁLISE PARCIAL' },

    { label: 'Análise Concluída', value: 'ANÁLISE CONCLUÍDA' }
];

const OPCOES_PE_FILTER = [
    { label: 'Incluir Pontos Estratégicos', value: 'com_pe' },
    { label: 'Sem Pontos Estratégicos', value: 'sem_pe' },
    { label: 'Apenas Pontos Estratégicos', value: 'so_pe' }
];
// Lista de Localidades carregada dinamicamente do Firestore (via useTodasLocalidades)

// --- Funções de Cálculo (Mantidas) ---
const calcularResumos = (visitas) => {
    const resumo = {
        imoveisTrabalhados: { R: 0, C: 0, TB: 0, PE: 0, O: 0, Total: 0 },
        statusImoveis: { INSPECIONADO: 0, RECUPERADO: 0, RECUSA: 0, FECHADO: 0 },
        depositosInspecionados: { A1: 0, A2: 0, B: 0, C: 0, D1: 0, D2: 0, E: 0, Total: 0 },
        depositosEliminados: 0,
        tubitosColetados: 0,
        tratamentoFocal: { imoveis: 0, gramas: 0, depositos: 0 },
        tratamentoPerifocal: { imoveis: 0, gramas: 0 },
        quarteiroesTrabalhados: new Set(),
        resumoAnimais: {
            total: 0, caes: 0, gatos: 0, machos: 0, femeas: 0, emResidencias: 0, emComercios: 0,
        }
    };

    for (const v of visitas) {
        if (v.tipoImovel && resumo.imoveisTrabalhados.hasOwnProperty(v.tipoImovel)) {
            resumo.imoveisTrabalhados[v.tipoImovel]++;
            resumo.imoveisTrabalhados.Total++;
        }
        if (v.visitaStatus) {
            const status = v.visitaStatus;
            if (resumo.statusImoveis.hasOwnProperty(status)) {
                resumo.statusImoveis[status]++;
            }
        }
        if (v.depositos) {
            for (const tipo of ['A1', 'A2', 'B', 'C', 'D1', 'D2', 'E']) {
                const count = Number(v.depositos[tipo]) || 0;
                if (count > 0) {
                    resumo.depositosInspecionados[tipo] += count;
                    resumo.depositosInspecionados.Total += count;
                }
            }
        }
        resumo.depositosEliminados += Number(v.depositoEliminado) || 0;
        resumo.tubitosColetados += Number(v.qtdeTubitos) || (v.amostras ? v.amostras.length : 0);
        if (v.tratamentosFocais && v.tratamentosFocais.length > 0) {
            resumo.tratamentoFocal.imoveis++;
            resumo.tratamentoFocal.depositos += v.tratamentosFocais.length;
            v.tratamentosFocais.forEach(trat => {
                resumo.tratamentoFocal.gramas += Number(trat.qtdeGramas) || 0;
            });
        }
        if (v.tratamentosPerifocais && v.tratamentosPerifocais.length > 0) {
            resumo.tratamentoPerifocal.imoveis++;
            v.tratamentosPerifocais.forEach(trat => {
                resumo.tratamentoPerifocal.gramas += Number(trat.qtdeGramas) || 0;
            });
        }
        if (v.quarteirao) {
            resumo.quarteiroesTrabalhados.add(v.quarteirao);
        }

        if (v.temAnimais === 'SIM' && v.animais) {
            for (const animal of v.animais) {
                const quantidade = Number(animal.quantidade) || 0;
                resumo.resumoAnimais.total += quantidade;
                if (animal.tipo === 'CAO') resumo.resumoAnimais.caes += quantidade;
                if (animal.tipo === 'GATO') resumo.resumoAnimais.gatos += quantidade;
                if (animal.sexo === 'MACHO') resumo.resumoAnimais.machos += quantidade;
                if (animal.sexo === 'FEMEA') resumo.resumoAnimais.femeas += quantidade;
                if (v.tipoImovel === 'R') resumo.resumoAnimais.emResidencias += quantidade;
                if (v.tipoImovel === 'C') resumo.resumoAnimais.emComercios += quantidade;
            }
        }
    }
    return resumo;
};

const gerarAnaliseDetalhada = (visitas) => {
    const analise = {
        exemplares: {
            aegypti: { larvas: 0, pupas: 0, exuvia: 0, adultos: 0, total: 0 },
            albopictus: { larvas: 0, pupas: 0, exuvia: 0, adultos: 0, total: 0 },
            outros: { larvas: 0, pupas: 0, exuvia: 0, adultos: 0, total: 0 },
        },
        depositosPositivos: {
            A1: { aegypti: 0, albopictus: 0, outros: 0, total: 0 }, A2: { aegypti: 0, albopictus: 0, outros: 0, total: 0 },
            B: { aegypti: 0, albopictus: 0, outros: 0, total: 0 }, C: { aegypti: 0, albopictus: 0, outros: 0, total: 0 },
            D1: { aegypti: 0, albopictus: 0, outros: 0, total: 0 }, D2: { aegypti: 0, albopictus: 0, outros: 0, total: 0 },
            E: { aegypti: 0, albopictus: 0, outros: 0, total: 0 }, Total: 0
        },
        imoveisPositivos: {
            R: { aegypti: 0, albopictus: 0, outros: 0, total: 0 }, C: { aegypti: 0, albopictus: 0, outros: 0, total: 0 },
            TB: { aegypti: 0, albopictus: 0, outros: 0, total: 0 }, PE: { aegypti: 0, albopictus: 0, outros: 0, total: 0 },
            O: { aegypti: 0, albopictus: 0, outros: 0, total: 0 }, Total: 0
        },
        localidades: {},
    };

    const imoveisJaContados = new Set();

    for (const v of visitas) {
        if (!v.amostras || v.amostras.length === 0) continue;

        for (const amostra of v.amostras) {
            // Verifica se o status é concluído (considerando variações de caixa alta/baixa e acentuação)
            const statusAnalise = amostra.analise?.status ? String(amostra.analise.status).toUpperCase() : '';
            if (!amostra.analise || (statusAnalise !== 'CONCLUIDO' && statusAnalise !== 'CONCLUÍDO')) continue;

            const { aegypti, albopictus, outros } = amostra.analise;
            const especies = { aegypti, albopictus, outros };

            for (const [nomeEspecie, exemplares] of Object.entries(especies)) {
                if (!exemplares) continue;
                const totalExemplaresNaAmostra = (exemplares.larvas || 0) + (exemplares.pupas || 0) + (exemplares.exuvia || 0) + (exemplares.adultos || 0);

                if (totalExemplaresNaAmostra > 0) {
                    // Contagem por Estágio
                    analise.exemplares[nomeEspecie].larvas += exemplares.larvas || 0;
                    analise.exemplares[nomeEspecie].pupas += exemplares.pupas || 0;
                    analise.exemplares[nomeEspecie].exuvia += exemplares.exuvia || 0;
                    analise.exemplares[nomeEspecie].adultos += exemplares.adultos || 0;

                    // Contagem detalhada por Depósito
                    const dep = amostra.depositoColeta;
                    if (dep && analise.depositosPositivos[dep]) {
                        analise.depositosPositivos[dep][nomeEspecie]++;
                    }

                    // Contagem detalhada por Imóvel
                    const imovelId = v.id;
                    const chaveContagemImovel = `${imovelId}-${nomeEspecie}`;
                    if (!imoveisJaContados.has(chaveContagemImovel)) {
                        const imovel = v.tipoImovel;
                        if (imovel && analise.imoveisPositivos[imovel]) {
                            analise.imoveisPositivos[imovel][nomeEspecie]++;
                            imoveisJaContados.add(chaveContagemImovel);
                        }
                    }

                    // Contagem por Localidade/Quarteirão (já estava correta)
                    const local = v.localidade;
                    const quart = v.quarteirao;
                    if (!analise.localidades[local]) analise.localidades[local] = {};
                    if (!analise.localidades[local][quart]) analise.localidades[local][quart] = { aegypti: 0, albopictus: 0, outros: 0 };
                    analise.localidades[local][quart][nomeEspecie]++;
                }
            }
        }
    }

    // Calcula todos os totais no final
    for (const especie of Object.keys(analise.exemplares)) {
        analise.exemplares[especie].total = analise.exemplares[especie].larvas + analise.exemplares[especie].pupas + analise.exemplares[especie].exuvia + analise.exemplares[especie].adultos;
    }
    for (const tipo of Object.keys(analise.depositosPositivos)) {
        if (tipo !== 'Total') {
            const item = analise.depositosPositivos[tipo];
            item.total = item.aegypti + item.albopictus + item.outros;
            analise.depositosPositivos.Total += item.total;
        }
    }
    for (const tipo of Object.keys(analise.imoveisPositivos)) {
        if (tipo !== 'Total') {
            const item = analise.imoveisPositivos[tipo];
            item.total = item.aegypti + item.albopictus + item.outros;
            analise.imoveisPositivos.Total += item.total;
        }
    }

    return analise;
};

// --- Componente de Seleção Múltipla com lógica de Dropdown (CORRIGIDO) ---
const MultiSelectFilter = ({ label, options, selectedValues, onChange }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    // Fecha ao clicar fora do componente
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);


    const handleToggle = (value) => {
        onChange(
            selectedValues.includes(value)
                ? selectedValues.filter(v => v !== value)
                : [...selectedValues, value]
        );
    };

    const handleSelectAll = (isSelectingAll) => {
        // Mapeia para pegar o valor correto, seja de string ou objeto {value, label}
        onChange(isSelectingAll ? options.map(o => o.value || o) : []);
    };

    const allSelected = selectedValues.length === options.length && options.length > 0;
    const displayLabel = selectedValues.length === 0
        ? `Todos os ${label}`
        : `${selectedValues.length} Selecionado(s)`;

    return (
        <div className="input-group multi-select-filter" ref={dropdownRef}>
            <label>{label}</label>
            <div className="dropdown">
                {/* Botão para ABRIR/FECHAR o Filtro */}
                <button
                    className="dropdown-toggle"
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    {displayLabel}
                </button>
                {/* Menu com Checkboxes - VISÍVEL APENAS se isOpen for TRUE */}
                {isOpen && (
                    <div className="dropdown-menu">
                        {/* Checkbox para Selecionar/Deselecionar Todos */}
                        <label>
                            <input
                                type="checkbox"
                                checked={allSelected}
                                onChange={() => handleSelectAll(!allSelected)}
                            />
                            **Selecionar Todos**
                        </label>
                        <hr />
                        {/* Checkboxes individuais */}
                        {options.map(option => (
                            <label key={option.value || option}>
                                <input
                                    type="checkbox"
                                    value={option.value || option}
                                    checked={selectedValues.includes(option.value || option)}
                                    onChange={() => handleToggle(option.value || option)}
                                />
                                {option.label || option}
                            </label>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};


// --- Componente Principal ResumoGeral ---

function ResumoGeral({ user }) {
    const { localidades: todasLocalidades } = useTodasLocalidades();
    const [agentsList, setAgentsList] = useState([]);
    const [selectedAgents, setSelectedAgents] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState([]);
    const [selectedLocalidades, setSelectedLocalidades] = useState([]);
    const [selectedPEFilter, setSelectedPEFilter] = useState('com_pe');

    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

    // Função para calcular a data limite (máximo 7 dias de intervalo)
    const getMaxDate = (start) => {
        // Se for Master, sem limite de data
        if (user?.perfil === 'master') return '';

        if (!start) return '';
        const d = new Date(start);
        d.setDate(d.getDate() + 6); // 7 dias de intervalo (inclusive)
        return d.toISOString().split('T')[0];
    };

    // Handler para mudança na Data Inicial
    const handleStartDateChange = (e) => {
        const newStart = e.target.value;
        setStartDate(newStart);

        // Se for Master, não trava o calendário
        if (user?.perfil === 'master') return;

        // Se a data final atual for menor que a nova data inicial ou exceder o limite de 7 dias, ajusta automaticamente
        if (newStart && endDate) {
            const maxDate = getMaxDate(newStart);
            if (endDate < newStart) {
                setEndDate(newStart);
            } else if (endDate > maxDate) {
                setEndDate(maxDate);
            }
        }
    };

    const [cachedVisitas, setCachedVisitas] = useState([]);
    const [dadosResumo, setDadosResumo] = useState(null);
    const [analiseDetalhada, setAnaliseDetalhada] = useState(null);
    const [loading, setLoading] = useState(false);
    const [lastFetchParams, setLastFetchParams] = useState(null); // Para mostrar o que foi baixado

    // Carrega a lista de agentes na montagem do componente
    useEffect(() => {
        const fetchAgents = async () => {
            try {
                const q = query(collection(firestore, "usuarios"), where("perfil", "==", "agente"));
                const querySnapshot = await getDocs(q);
                const agents = querySnapshot.docs.map(doc => ({ uid: doc.id, ...doc.data() }));
                setAgentsList(agents);
                setSelectedAgents(agents.map(a => a.uid));
            } catch (error) {
                console.error("Erro ao buscar agentes: ", error);
                alert("Não foi possível carregar a lista de agentes.");
            }
        };
        fetchAgents();
        setSelectedStatus(OPCOES_STATUS.map(o => o.value));
        // Localidades são carregadas via hook, inicializadas no useEffect abaixo
    }, [user]);

    // Inicializa localidades selecionadas após o hook carregar
    useEffect(() => {
        if (todasLocalidades.length === 0) return;
        if (user?.perfil === 'master') {
            setSelectedLocalidades(todasLocalidades.map(l => l.nome));
        } else {
            setSelectedLocalidades([]); // Gestor começa vazio para forçar escolha consciente
        }
    }, [todasLocalidades, user]);

    // --- 1. BUSCA NO BANCO (Apenas Datas) ---
    const handleFetchData = async () => {
        if (!startDate || !endDate) {
            alert("Por favor, selecione a data de início e de fim.");
            return;
        }


        // Math.ceil com end=23:59:59 e start=00:00:00 de uma semana (dom-sáb) = 7. Limite correto é > 7.
        const diffTime = Math.abs((new Date(endDate)) - (new Date(startDate)));
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (user?.perfil !== 'master' && diffDays > 7) {
            alert("O período máximo de busca é de 7 dias.");
            return;
        }

        setLoading(true);
        setCachedVisitas([]);
        setDadosResumo(null);
        setAnaliseDetalhada(null);

        const [sYear, sMonth, sDay] = startDate.split('-');
        const start = new Date(sYear, sMonth - 1, sDay);
        start.setHours(0, 0, 0, 0);

        const [eYear, eMonth, eDay] = endDate.split('-');
        const end = new Date(eYear, eMonth - 1, eDay);
        end.setHours(23, 59, 59, 999);



        let q;
        const localidadeAlvo = selectedLocalidades.length === 1 ? selectedLocalidades[0] : null;

        // OTIMIZAÇÃO: Filtra por Localidade no Servidor
        // IMPORTANTE: Converte para MAIÚSCULO ("1 - ALAMAR") pois o banco guarda assim.
        if (localidadeAlvo && user?.perfil !== 'master') {
            const localidadeDB = localidadeAlvo.toUpperCase(); // Força maiúsculo para casar com o banco

            q = query(
                collection(firestore, 'relatoriosDiarios_FAD1'),
                where('dataAtividade', '>=', start),
                where('dataAtividade', '<=', end),
                where('localidade', '==', localidadeDB)
            );
            console.log(`[ResumoGeral] Otimização Ativa: Buscando localidade '${localidadeDB}' no servidor.`);

        } else {
            // Busca ampla
            q = query(
                collection(firestore, 'relatoriosDiarios_FAD1'),
                where('dataAtividade', '>=', start),
                where('dataAtividade', '<=', end)
            );
        }



        try {
            const querySnapshot = await getDocs(q);
            let visitas = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Filtrar visitas canceladas (Mais robusto)
            visitas = visitas.filter(v => {
                const status = v.statusRegistro ? v.statusRegistro.trim().toLowerCase() : 'ativo';
                return status === 'ativo';
            });

            console.log(`[ResumoGeral] Baixados ${visitas.length} registros (excluindo canceladas).`);

            setCachedVisitas(visitas);
            setLastFetchParams({
                start: startDate,
                end: endDate,
                count: visitas.length,
                loc: user?.perfil !== 'master' ? selectedLocalidades[0] : 'Todas'
            });

        } catch (error) {
            console.error("Erro ao buscar dados:", error);
            alert("Erro ao buscar dados no banco.");
        }
        setLoading(false);
    };

    // --- 2. FILTRAGEM LOCAL (Memória) ---
    // Roda automaticamente sempre que o Cache ou os Filtros mudam
    useEffect(() => {
        // if (cachedVisitas.length === 0) return; // REMOVIDO: Permitir calcular zeros se array vazio


        const filtrarEProcessar = () => {
            let visitas = cachedVisitas.filter(v => {
                // Filtro de Agente
                if (selectedAgents.length > 0 && !selectedAgents.includes(v.idAgente)) return false;

                // Filtro de Status
                if (selectedStatus.length > 0) {
                    const statusVisita = v.visitaStatus ? String(v.visitaStatus).trim().toUpperCase() : '';
                    const statusLab = v.status ? String(v.status).trim().toUpperCase() : '';
                    if (!selectedStatus.includes(statusVisita) && !selectedStatus.includes(statusLab)) {
                        return false;
                    }
                }

                // Filtro de Pontos Estratégicos
                if (selectedPEFilter === 'sem_pe' && v.tipoImovel === 'PE') return false;
                if (selectedPEFilter === 'so_pe' && v.tipoImovel !== 'PE') return false;

                // Filtro de Localidade
                if (selectedLocalidades.length > 0) {
                    const localidadeNormalizada = normalizarTexto(v.localidade);
                    const match = selectedLocalidades.some(loc => normalizarTexto(loc) === localidadeNormalizada);
                    if (!match) return false;
                }

                return true;
            });

            console.log(`[ResumoGeral] Filtrado localmente: ${visitas.length} registros.`);

            setDadosResumo(calcularResumos(visitas));
            setAnaliseDetalhada(gerarAnaliseDetalhada(visitas));
        };

        filtrarEProcessar();

    }, [cachedVisitas, selectedAgents, selectedStatus, selectedLocalidades, selectedPEFilter]);

    // Função de Exportação (Mantida, com ajustes nos cabeçalhos)
    const handleExportCSV = () => {
        if (!dadosResumo || !analiseDetalhada) {
            alert("Gere um resumo antes de exportar.");
            return;
        }

        const agenteInfo = selectedAgents.length === agentsList.length
            ? "Todos os Agentes"
            : agentsList
                .filter(a => selectedAgents.includes(a.uid))
                .map(a => a.nome || a.email)
                .join(', ');

        const statusInfo = selectedStatus.length === OPCOES_STATUS.length
            ? "Todos os Status"
            : selectedStatus.join(', ');

        const localidadeInfo = selectedLocalidades.length === todasLocalidades.length
            ? "Todas as Localidades"
            : selectedLocalidades.join(', ');

        const peFilterInfo = OPCOES_PE_FILTER.find(o => o.value === selectedPEFilter)?.label || selectedPEFilter;

        const titulo = [
            ["RELATÓRIO GERAL DE ATIVIDADES"],
            [`Período de ${startDate} a ${endDate}`],
            [`Filtro P.E.: ${peFilterInfo}`],
            [`Agentes: ${agenteInfo}`],
            [`Status: ${statusInfo}`],
            [`Localidades: ${localidadeInfo}`],
        ];

        const imoveis = [
            ["IMÓVEIS TRABALHADOS"],
            ["Quarteirões Trabalhados", dadosResumo.quarteiroesTrabalhados.size],
        ];

        const status = [
            ["STATUS DOS IMÓVEIS"],
            ["Status", "Quantidade"],
            ["Trabalhados", dadosResumo.statusImoveis.INSPECIONADO],
            ["Recuperados", dadosResumo.statusImoveis.RECUPERADO],
            ["Recusas", dadosResumo.statusImoveis.RECUSA],
            ["Fechados", dadosResumo.statusImoveis.FECHADO],
        ];

        const depositos = [
            ["DEPÓSITOS INSPECIONADOS"],
            ["Tipo", "Quantidade"],
            ["A1", dadosResumo.depositosInspecionados.A1],
            ["A2", dadosResumo.depositosInspecionados.A2],
            ["B", dadosResumo.depositosInspecionados.B],
            ["C", dadosResumo.depositosInspecionados.C],
            ["D1", dadosResumo.depositosInspecionados.D1],
            ["D2", dadosResumo.depositosInspecionados.D2],
            ["E", dadosResumo.depositosInspecionados.E],
        ];

        const atividades = [
            ["ATIVIDADES GERAIS"],
            ["Item", "Quantidade"],
            ["Depósitos Eliminados", dadosResumo.depositosEliminados],
            ["Tubitos Coletados", dadosResumo.tubitosColetados],
        ];

        const tratamentoFocal = [
            ["TRATAMENTO FOCAL (LARVICIDA)"],
            ["Item", "Quantidade"],
            ["Imóveis Tratados", dadosResumo.tratamentoFocal.imoveis],
            ["Depósitos Tratados", dadosResumo.tratamentoFocal.depositos],
            ["Gramas de Larvicida", dadosResumo.tratamentoFocal.gramas],
        ];

        const tratamentoPerifocal = [
            ["TRATAMENTO PERIFOCAL (ADULTICIDA)"],
            ["Item", "Quantidade"],
            ["Imóveis Tratados", dadosResumo.tratamentoPerifocal.imoveis],
            ["Gramas de Adulticida", dadosResumo.tratamentoPerifocal.gramas],
        ];

        const animais = [
            ["CENSO ANIMAL"],
            ["Item", "Quantidade"],
            ["Total de Animais", dadosResumo.resumoAnimais.total],
            ["Cães", dadosResumo.resumoAnimais.caes],
            ["Gatos", dadosResumo.resumoAnimais.gatos],
            ["Machos", dadosResumo.resumoAnimais.machos],
            ["Fêmeas", dadosResumo.resumoAnimais.femeas],
        ];

        const exemplaresLab = [
            ["EXEMPLARES POR ESPÉCIE (LAB)"],
            ["Espécie", "Total", "Larvas", "Pupas", "Exúvias", "Adultos"],
            ["A. aegypti", analiseDetalhada.exemplares.aegypti.total, analiseDetalhada.exemplares.aegypti.larvas, analiseDetalhada.exemplares.aegypti.pupas, analiseDetalhada.exemplares.aegypti.exuvia, analiseDetalhada.exemplares.aegypti.adultos],
            ["A. albopictus", analiseDetalhada.exemplares.albopictus.total, analiseDetalhada.exemplares.albopictus.larvas, analiseDetalhada.exemplares.albopictus.pupas, analiseDetalhada.exemplares.albopictus.exuvia, analiseDetalhada.exemplares.albopictus.adultos],
            ["Outros", analiseDetalhada.exemplares.outros.total, analiseDetalhada.exemplares.outros.larvas, analiseDetalhada.exemplares.outros.pupas, analiseDetalhada.exemplares.outros.exuvia, analiseDetalhada.exemplares.outros.adultos],
        ];

        const depositosLab = [
            ["DEPÓSITOS COM ESPÉCIMES (LAB)"],
            ["Tipo", "Total", "A. aegypti", "A. albopictus", "Outros"],
        ];
        for (const [tipo, especies] of Object.entries(analiseDetalhada.depositosPositivos)) {
            if (tipo !== 'Total') {
                const total = especies.aegypti + especies.albopictus + especies.outros;
                if (total > 0) {
                    depositosLab.push([tipo, total, especies.aegypti, especies.albopictus, especies.outros]);
                }
            }
        }
        depositosLab.push(["TOTAL GERAL", analiseDetalhada.depositosPositivos.Total]);


        const imoveisLab = [
            ["IMÓVEIS COM ESPÉCIMES (LAB)"],
            ["Tipo", "Total", "A. aegypti", "A. albopictus", "Outros"],
        ];
        for (const [tipo, especies] of Object.entries(analiseDetalhada.imoveisPositivos)) {
            if (tipo !== 'Total') {
                const total = especies.aegypti + especies.albopictus + especies.outros;
                if (total > 0) {
                    imoveisLab.push([
                        tipo,
                        total,
                        especies.aegypti,
                        especies.albopictus,
                        especies.outros
                    ]);
                }
            }
        }
        imoveisLab.push(["TOTAL GERAL", analiseDetalhada.imoveisPositivos.Total]);

        const localidadesLab = [
            ["POSITIVIDADE POR LOCALIDADE/QUARTEIRÃO (LAB)"],
            ["Localidade", "Quarteirão", "Imóveis Positivos (A. aegypti)", "Imóveis Positivos (A. albopictus)"],
        ];

        for (const [local, quarteiroes] of Object.entries(analiseDetalhada.localidades)) {
            for (const [quart, especies] of Object.entries(quarteiroes)) {
                localidadesLab.push([
                    local,
                    quart,
                    especies.aegypti,
                    especies.albopictus,
                ]);
            }
        }

        // --- Montagem da Planilha ---
        const worksheet = XLSX.utils.aoa_to_sheet(titulo);

        // Ajusta o 'origin' para A6 (já que adicionamos mais 3 linhas de informações de filtro)
        XLSX.utils.sheet_add_aoa(worksheet, imoveis, { origin: 'A7' }); // Ajustado para A7 devido às 5 linhas de título
        XLSX.utils.sheet_add_aoa(worksheet, status, { origin: 'D7' });
        XLSX.utils.sheet_add_aoa(worksheet, depositos, { origin: 'G7' });
        XLSX.utils.sheet_add_aoa(worksheet, atividades, { origin: 'J7' });
        XLSX.utils.sheet_add_aoa(worksheet, animais, { origin: 'M7' });
        XLSX.utils.sheet_add_aoa(worksheet, tratamentoFocal, { origin: 'P7' });
        XLSX.utils.sheet_add_aoa(worksheet, tratamentoPerifocal, { origin: 'S7' });

        XLSX.utils.sheet_add_aoa(worksheet, exemplaresLab, { origin: 'V7' });
        XLSX.utils.sheet_add_aoa(worksheet, depositosLab, { origin: 'AC7' });
        XLSX.utils.sheet_add_aoa(worksheet, imoveisLab, { origin: 'AI7' });
        XLSX.utils.sheet_add_aoa(worksheet, localidadesLab, { origin: 'AO7' });

        // Ajusta a largura das colunas (mantida)
        worksheet['!cols'] = [
            { wch: 25 }, { wch: 10 }, { wch: 2 }, { wch: 25 }, { wch: 10 }, { wch: 2 },
            { wch: 25 }, { wch: 10 }, { wch: 2 }, { wch: 25 }, { wch: 10 }, { wch: 2 },
            { wch: 25 }, { wch: 10 }, { wch: 2 }, { wch: 28 }, { wch: 10 }, { wch: 2 },
            { wch: 32 }, { wch: 10 }, { wch: 2 }, { wch: 25 }, { wch: 10 }, { wch: 10 },
            { wch: 10 }, { wch: 10 }, { wch: 10 }, { wch: 2 }, { wch: 28 }, { wch: 10 },
            { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 2 }, { wch: 25 }, { wch: 10 },
            { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 2 }, { wch: 25 }, { wch: 12 },
            { wch: 25 }, { wch: 28 }
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Resumo Geral");

        XLSX.writeFile(workbook, `Resumo_Dengue_${startDate}_a_${endDate}.xlsx`);
    };

    return (
        <div className="dashboard-container">
            <h3>Resumo Geral de Atividades</h3>

            <div className="filters-container" style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>

                {/* --- SEÇÃO 1: FETCH / BANCO DE DADOS --- */}
                <div style={{ padding: '15px', backgroundColor: '#eef2f7', borderRadius: '8px', border: '1px solid #cce' }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#335' }}>1. Seleção de Dados</h4>

                    <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                        {/* SELEÇÃO DE LOCALIDADE (Obrigatória para Gestor agora) */}
                        <div style={{ flex: 1, minWidth: '200px' }}>
                            {user?.perfil === 'master' ? (
                                <div className="input-group">
                                    <label>Localidade (Opcional para Master)</label>
                                    <div style={{ padding: '10px', backgroundColor: '#ddd', borderRadius: '4px', color: '#555', fontSize: '0.9em' }}>
                                        Master baixa tudo por período
                                    </div>
                                </div>
                            ) : (
                                <div className="input-group">
                                    <label>Localidade (Obrigatório)</label>
                                    <select
                                        value={selectedLocalidades[0] || ''}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setSelectedLocalidades(val ? [val] : []);
                                        }}
                                        style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ddd', width: '100%' }}
                                    >
                                        <option value="">Selecione uma localidade...</option>
                                        {todasLocalidades.map(loc => (
                                            <option key={loc.id} value={loc.nome}>{loc.nome}</option>
                                        ))}
                                    </select>
                                </div>
                            )}
                        </div>

                        <div className="input-group">
                            <label>Início</label>
                            <input
                                type="date"
                                value={startDate}
                                onChange={handleStartDateChange}
                            />
                        </div>
                        <div className="input-group">
                            <label>Fim (Máx. 7d)</label>
                            <input
                                type="date"
                                value={endDate}
                                onChange={e => setEndDate(e.target.value)}
                                min={startDate}
                                max={getMaxDate(startDate)}
                            />
                        </div>
                        <button
                            onClick={handleFetchData}
                            disabled={loading || (user?.perfil !== 'master' && selectedLocalidades.length === 0)}
                            style={{ backgroundColor: '#2c5282', color: 'white', height: '42px', marginBottom: '2px' }}
                        >
                            {loading ? 'Baixando...' : '📥 Baixar Dados'}
                        </button>
                    </div>
                    {lastFetchParams && (
                        <p style={{ fontSize: '0.85em', color: '#666', marginTop: '5px', marginBottom: 0 }}>
                            ✅ Último download: {lastFetchParams.count} registros.
                            Loc: <strong>{lastFetchParams.loc}</strong>.
                            ({lastFetchParams.start} a {lastFetchParams.end})
                        </p>
                    )}
                </div>

                {/* --- SEÇÃO 2: FILTROS LOCAIS --- */}
                <div style={{ padding: '15px', backgroundColor: '#fff', borderRadius: '8px', border: '1px solid #ddd' }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#555' }}>2. Filtros de Visualização</h4>

                    <div className="filter-row" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>

                        {/* LOCALIDADE e DATA (Topo visual, mas filtro lógico) */}
                        <div style={{ flex: 1, minWidth: '200px' }}>
                            {user?.perfil === 'master' ? (
                                <MultiSelectFilter
                                    label="Filtrar Localidade (Local)"
                                    options={todasLocalidades.map(l => l.nome)}
                                    selectedValues={selectedLocalidades}
                                    onChange={setSelectedLocalidades}
                                />
                            ) : (
                                <div style={{ fontSize: '0.9em', color: '#888', fontStyle: 'italic', padding: '10px' }}>
                                    Localidade já fixada no download: <strong>{selectedLocalidades[0]}</strong>
                                </div>
                            )}
                        </div>

                        <div style={{ flex: 1, minWidth: '200px' }}>
                            <MultiSelectFilter
                                label="Agente"
                                options={agentsList.map(agent => ({
                                    value: agent.uid,
                                    label: agent.nome || agent.email
                                }))}
                                selectedValues={selectedAgents}
                                onChange={setSelectedAgents}
                            />
                        </div>
                    </div>

                    <div className="filter-row" style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginTop: '10px' }}>
                        <div style={{ flex: 1 }}>
                            <MultiSelectFilter
                                label="Status"
                                options={OPCOES_STATUS}
                                selectedValues={selectedStatus}
                                onChange={setSelectedStatus}
                            />
                        </div>
                        <div style={{ flex: 1 }}>
                            <div className="input-group">
                                <label>Pontos Estratégicos</label>
                                <select
                                    value={selectedPEFilter}
                                    onChange={(e) => setSelectedPEFilter(e.target.value)}
                                    style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ddd', width: '100%' }}
                                >
                                    {OPCOES_PE_FILTER.map(option => (
                                        <option key={option.value} value={option.value}>{option.label}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div style={{ marginTop: '15px' }}>
                        <button
                            onClick={handleExportCSV}
                            className="export-button"
                            disabled={!dadosResumo || loading}
                            style={{ width: '100%' }}
                        >
                            Exportar Relatório Atual (Excel)
                        </button>
                    </div>
                </div>
            </div>

            {loading && <p>Calculando resumos...</p>}

            {/* --- Seção de Resumo (Mantida) --- */}
            {dadosResumo && (
                <div className="summary-grid">
                    {/* --- Card 1: Nº Imóveis trabalhados por tipo --- */}
                    <div className="summary-item">
                        <h4>Nº Imóveis trabalhados por tipo</h4>
                        <div className="resumo-details">
                            <p>Residências (R): <strong>{dadosResumo?.imoveisTrabalhados.R || 0}</strong></p>
                            <p>Comércios (C): <strong>{dadosResumo?.imoveisTrabalhados.C || 0}</strong></p>
                            <p>Terrenos Baldios (TB): <strong>{dadosResumo?.imoveisTrabalhados.TB || 0}</strong></p>
                            <p>Pontos Estratégicos (PE): <strong>{dadosResumo?.imoveisTrabalhados.PE || 0}</strong></p>
                            <p>Outros (O): <strong>{dadosResumo?.imoveisTrabalhados.O || 0}</strong></p>
                            <p className="total">Total: <strong>{dadosResumo?.imoveisTrabalhados.Total || 0}</strong></p>
                        </div>
                    </div>

                    {/* --- Card 2: Status dos Imóveis --- */}
                    <div className="summary-item">
                        <h4>Status dos Imóveis</h4>
                        <div className="resumo-details">
                            <p>Trabalhados: <strong>{dadosResumo?.statusImoveis.INSPECIONADO || 0}</strong></p>
                            <p>Recuperados: <strong>{dadosResumo?.statusImoveis.RECUPERADO || 0}</strong></p>
                            <p>Recusas: <strong>{dadosResumo?.statusImoveis.RECUSA || 0}</strong></p>
                            <p>Fechados: <strong>{dadosResumo?.statusImoveis.FECHADO || 0}</strong></p>
                        </div>
                    </div>

                    {/* --- Card 3: Depósitos Inspecionados --- */}
                    <div className="summary-item">
                        <h4>Nº depósitos inspecionados</h4>
                        <div className="resumo-details">
                            <p>A1: <strong>{dadosResumo?.depositosInspecionados.A1 || 0}</strong></p>
                            <p>A2: <strong>{dadosResumo?.depositosInspecionados.A2 || 0}</strong></p>
                            <p>B: <strong>{dadosResumo?.depositosInspecionados.B || 0}</strong></p>
                            <p>C: <strong>{dadosResumo?.depositosInspecionados.C || 0}</strong></p>
                            <p>D1: <strong>{dadosResumo?.depositosInspecionados.D1 || 0}</strong></p>
                            <p>D2: <strong>{dadosResumo?.depositosInspecionados.D2 || 0}</strong></p>
                            <p>E: <strong>{dadosResumo?.depositosInspecionados.E || 0}</strong></p>
                            <p className="total">Total: <strong>{dadosResumo?.depositosInspecionados.Total || 0}</strong></p>
                        </div>
                    </div>

                    {/* --- Card 4: Atividades Gerais --- */}
                    <div className="summary-item">
                        <h4>Atividades Gerais</h4>
                        <div className="resumo-details">
                            <p>Depósitos Eliminados: <strong>{dadosResumo?.depositosEliminados || 0}</strong></p>
                            <p>Tubitos Coletados: <strong>{dadosResumo?.tubitosColetados || 0}</strong></p>
                            <p>Quarteirões Trabalhados: <strong>{dadosResumo?.quarteiroesTrabalhados.size || 0}</strong></p>
                        </div>
                    </div>

                    {/* --- Card 5: Tratamento Focal --- */}
                    <div className="summary-item">
                        <h4>Tratamento Focal Larvicida</h4>
                        <div className="resumo-details">
                            <p>Imóveis Tratados: <strong>{dadosResumo?.tratamentoFocal.imoveis || 0}</strong></p>
                            <p>Depósitos Tratados: <strong>{dadosResumo?.tratamentoFocal.depositos || 0}</strong></p>
                            <p>Gramas de Larvicida: <strong>{dadosResumo?.tratamentoFocal.gramas || 0}</strong></p>
                        </div>
                    </div>

                    {/* --- Card 6: Tratamento Perifocal --- */}
                    <div className="summary-item">
                        <h4>Tratamento Perifocal Adulticida</h4>
                        <div className="resumo-details">
                            <p>Imóveis Tratados: <strong>{dadosResumo?.tratamentoPerifocal.imoveis || 0}</strong></p>
                            <p>Gramas de Adulticida: <strong>{dadosResumo?.tratamentoPerifocal.gramas || 0}</strong></p>
                        </div>
                    </div>

                    {/* --- Card 7: Censo Animal (o novo card) --- */}
                    <div className="summary-item">
                        <h4>Censo Animal</h4>
                        <div className="resumo-details">
                            <p>Cães: {dadosResumo?.resumoAnimais.caes || 0} | Gatos: {dadosResumo?.resumoAnimais.gatos || 0}</p>
                            <p>Machos: {dadosResumo?.resumoAnimais.machos || 0} | Fêmeas: {dadosResumo?.resumoAnimais.femeas || 0}</p>
                            <hr />
                            <p><strong>Total de Animais:</strong> {dadosResumo?.resumoAnimais.total || 0}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* --- Seção de Análise Detalhada (Mantida) --- */}
            {analiseDetalhada && (
                <>
                    {/* Cards: Lab */}
                    <div className="summary-gridLab">
                        {/* Card 1: Exemplares por Espécie */}
                        <div className="summary-itemLab">
                            <h4>Exemplares por Espécie</h4>
                            <div className="resumo-details">
                                <p><strong>A. aegypti:</strong> {analiseDetalhada.exemplares.aegypti.total}
                                    <span className="small-text"> (L:{analiseDetalhada.exemplares.aegypti.larvas}, P:{analiseDetalhada.exemplares.aegypti.pupas}, E:{analiseDetalhada.exemplares.aegypti.exuvia}, A:{analiseDetalhada.exemplares.aegypti.adultos})</span>
                                </p>
                                <p><strong>A. albopictus:</strong> {analiseDetalhada.exemplares.albopictus.total}
                                    <span className="small-text"> (L:{analiseDetalhada.exemplares.albopictus.larvas}, P:{analiseDetalhada.exemplares.albopictus.pupas}, E:{analiseDetalhada.exemplares.albopictus.exuvia}, A:{analiseDetalhada.exemplares.albopictus.adultos})</span>
                                </p>
                            </div>
                        </div>

                        {/* Card 2: Depósitos com Espécimes */}
                        <div className="summary-itemLab">
                            <h4>Depósitos com Espécimes</h4>
                            <div className="resumo-details">
                                {Object.entries(analiseDetalhada.depositosPositivos).map(([tipo, especies]) => {
                                    if (tipo === 'Total') return null;
                                    const total = especies.aegypti + especies.albopictus + especies.outros;
                                    return (
                                        <p key={tipo}>
                                            <strong>{tipo}:</strong> {total}
                                            <span className="small-text"> (Aeg: {especies.aegypti}, Alb: {especies.albopictus})</span>
                                        </p>
                                    );
                                })}
                                <p className="total">Total de Depósitos Positivos: <strong>{analiseDetalhada.depositosPositivos.Total || 0}</strong></p>
                            </div>
                        </div>

                        {/* Card 3: Imóveis com Espécimes */}
                        <div className="summary-itemLab">
                            <h4>Imóveis com Espécimes</h4>
                            <div className="resumo-details">
                                {Object.entries(analiseDetalhada.imoveisPositivos).map(([tipo, especies]) => {
                                    if (tipo === 'Total') return null;
                                    const total = especies.aegypti + especies.albopictus + especies.outros;
                                    return (
                                        <p key={tipo}>
                                            <strong>{tipo}:</strong> {total}
                                            <span className="small-text"> (Aeg: {especies.aegypti}, Alb: {especies.albopictus})</span>
                                        </p>
                                    );
                                })}
                                <p className="total">Total de Imóveis Positivos: <strong>{analiseDetalhada.imoveisPositivos.Total || 0}</strong></p>
                            </div>
                        </div>

                        {/* Card 4: Positividade por Localidade e Quarteirão */}
                        {Object.keys(analiseDetalhada.localidades).length > 0 && (
                            <div className="summary-itemLab">
                                <h4>Positividade por Localidade / Quarteirão</h4>
                                <div className="resumo-details">
                                    {Object.entries(analiseDetalhada.localidades).map(([local, quarteiroes]) => (
                                        <div key={local} className="location-group">
                                            <strong>{local}:</strong>
                                            {Object.entries(quarteiroes).map(([quart, especies]) => (
                                                <p key={quart} style={{ marginLeft: '1rem' }}>
                                                    - Quarteirão {quart}:
                                                    <span className="small-text"> (Aegypti: {especies.aegypti}, Albopictus: {especies.albopictus})</span>
                                                </p>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export default ResumoGeral;